import type { JSX, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
};

const base = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false
});

export function HomeIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

export function UsersIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M15 19c0-2.2 1.6-4 4-4s4 1.8 4 4" />
    </svg>
  );
}

export function ClockIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function WalletIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h14a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4H3" />
      <circle cx="16.5" cy="14" r="1.2" />
    </svg>
  );
}

export function BriefcaseIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M3 12h18" />
    </svg>
  );
}

export function CheckSquareIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  );
}

export function BarChartIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20H2" />
    </svg>
  );
}

export function SettingsIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 0 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 0 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 0 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 0 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

export function DocumentIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
    </svg>
  );
}

export function UserPlusIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <circle cx="10" cy="8" r="3.2" />
      <path d="M3 19c0-3 2.7-5 7-5s7 2 7 5" />
      <path d="M19 7v6" />
      <path d="M22 10h-6" />
    </svg>
  );
}

export function BellIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  );
}

export function GiftIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <rect x="3" y="9" width="18" height="11" rx="2" />
      <path d="M3 13h18" />
      <path d="M12 9v11" />
      <path d="M8 9a2.5 2.5 0 1 1 4-2.5A2.5 2.5 0 1 1 16 9z" />
    </svg>
  );
}

export function ShieldIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function ChartIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M3 17l5-5 4 3 8-9" />
      <path d="M20 6h-4" />
      <path d="M20 6v4" />
    </svg>
  );
}

export function DocumentReportIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13v3" />
      <path d="M12 11v5" />
      <path d="M15 14v2" />
    </svg>
  );
}

export function TrophyIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...base(size)} {...rest}>
      <path d="M7 4h10v4a5 5 0 0 1-10 0z" />
      <path d="M5 6H3a3 3 0 0 0 3 3" />
      <path d="M19 6h2a3 3 0 0 1-3 3" />
      <path d="M9 18h6l-1 3h-4z" />
      <path d="M12 13v5" />
    </svg>
  );
}

export type IconKey =
  | 'home'
  | 'users'
  | 'clock'
  | 'wallet'
  | 'briefcase'
  | 'check'
  | 'bar'
  | 'settings'
  | 'document'
  | 'user-plus'
  | 'bell'
  | 'gift'
  | 'shield'
  | 'chart'
  | 'document-report'
  | 'trophy';

const ICON_MAP: Record<IconKey, (size: number) => JSX.Element> = {
  home: (size) => <HomeIcon size={size} />,
  users: (size) => <UsersIcon size={size} />,
  clock: (size) => <ClockIcon size={size} />,
  wallet: (size) => <WalletIcon size={size} />,
  briefcase: (size) => <BriefcaseIcon size={size} />,
  check: (size) => <CheckSquareIcon size={size} />,
  bar: (size) => <BarChartIcon size={size} />,
  settings: (size) => <SettingsIcon size={size} />,
  document: (size) => <DocumentIcon size={size} />,
  'user-plus': (size) => <UserPlusIcon size={size} />,
  bell: (size) => <BellIcon size={size} />,
  gift: (size) => <GiftIcon size={size} />,
  shield: (size) => <ShieldIcon size={size} />,
  chart: (size) => <ChartIcon size={size} />,
  'document-report': (size) => <DocumentReportIcon size={size} />,
  trophy: (size) => <TrophyIcon size={size} />
};

export function renderIcon(key: IconKey, size = 24): JSX.Element {
  const factory = ICON_MAP[key];
  return factory(size);
}